import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { AxiosError } from 'axios';
import { cloneDeep } from 'lodash';
import omitDeep from 'omit-deep';
import { useMutation } from '@apollo/client';
import { useBackdropContext } from '../../../../../context/backdrop';
import { useInstallationContext } from '../../../../../context/installation';
import {
  TariffConfiguration,
  TariffKindInternal,
  UpdateTariffConfigurationsMutation,
  UpdateTariffConfigurationsMutationVariables,
} from '../../../../../data-access/gql-types/graphql';
import { UPDATE_TARIFF_CONFIGURATIONS } from '../../../../../data-access/mutations/channels';
import { useMutationErrors } from '../../../../../hooks/errors/use-mutation-errors';
import { MutationErrorType } from '../../../../../types/common';
import { leadingZero } from '../../../../../utils/helpers';
import { toastError, toastSuccess } from '../../../../../utils/toast';
import { useMeterSetTariff } from '../../../api/meter/meter.hooks';
import { useDevicesAndChannelsContext } from '../../../context/devices-and-channels';
import { ChannelType } from '../../../types/channel';
import { ChannelMeterType } from '../../../types/channel/meter';
import { TimeZoneKindInternalRequest } from '../../../types/graphql-rest-converter';
import { TariffItem } from '../../../types/tariffs';
import { prepareOperatorAndTariffForRequest } from '../../channel-details/optimizer/configuration/utils';
import { tariffKind } from '../../channel-details/optimizer/configuration/utils/tariff-kind';
import { useTariffConfigurationContext } from '../context/tariff-configuration-context';
import { getTariffDataByEnum } from '../utils';
import { SetPricesData } from './use-meter-tariff-prices-configuration';
import { useMeterTariffErrors } from './use-meter-tariff-validation';

export const useMeterTariffConfiguration = () => {
  const { t } = useTranslation('channel-settings');
  const { t: tc } = useTranslation('common');
  const { channelId } = useParams<{ channelId: string }>();
  const { turnOnBackdrop, turnOffBackdrop } = useBackdropContext();
  const { mutate } = useMeterSetTariff();
  const { selectedInstallationId } = useInstallationContext();
  const { handleMeterTariffErrors } = useMeterTariffErrors();
  const { channel, updateChannelDetails } = useDevicesAndChannelsContext();
  const { handleErrors } = useMutationErrors(MutationErrorType.Channel);
  const [updateTariffConfigurations] = useMutation<
    UpdateTariffConfigurationsMutation,
    UpdateTariffConfigurationsMutationVariables
  >(UPDATE_TARIFF_CONFIGURATIONS);
  const {
    form: { setValue, getValues },
    selectedTariff,
  } = useTariffConfigurationContext();

  const setMeterTariffData = (
    kind: TariffKindInternal,
    tariffs: TariffItem[],
    handleSetPrices?: (data: SetPricesData) => void,
  ) => {
    const tariffData = getTariffDataByEnum(tariffs, kind);
    const data = tariffKind[kind];

    setValue('operator', data.operator);
    setValue('tariffName', data.tariff);

    if (tariffData) {
      setValue('wholeWeekendCheaper', tariffData.wholeWeekendCheaper);
      setValue('wholeHolidaysCheaper', tariffData.wholeHolidaysCheaper);
      setValue('ignoreSummerTimeChanges', tariffData.ignoreSummerTimeChanges);
      setValue(
        'hoursRanges',
        tariffData.hoursRanges.map((x) => ({
          ...x,
          from: `${leadingZero(x.from)}:00`,
          to: `${leadingZero(x.to)}:00`,
        })),
      );

      handleSetPrices?.({ selectedTariff: kind, hoursRanges: tariffData.hoursRanges });
    }
  };

  const checkIfItsTheSameTariff = (channel: ChannelType) => {
    const isTheSameTariff = (channel.data as ChannelMeterType).tariff?.kind === selectedTariff;

    if (isTheSameTariff) {
      toastSuccess({ content: t('toast.editConfigurationSuccess') });
      return true;
    }

    return false;
  };

  const handleSaveTariff = (onSuccess: () => void) => {
    if (!channelId || !channel) return;
    if (checkIfItsTheSameTariff(channel)) return;

    turnOnBackdrop();
    const values = getValues();

    mutate(
      {
        channelId,
        tariff: {
          wholeWeekendCheaper: values.wholeWeekendCheaper,
          wholeHolidaysCheaper: values.wholeHolidaysCheaper,
          ignoreSummerTimeChanges: values.ignoreSummerTimeChanges,
          hoursRanges: values.hoursRanges.map((range) => ({
            from: parseInt(range.from.replace(':00', '')),
            to: parseInt(range.to.replace(':00', '')),
            timeZoneKind: TimeZoneKindInternalRequest[range.timeZoneKind],
            pricingTier: range.pricingTier,
          })),
          kind: prepareOperatorAndTariffForRequest(values.operator, values.tariffName),
        },
      },
      {
        onSuccess: () => {
          onSuccess();
        },
        onError: (error: AxiosError) => {
          turnOffBackdrop();
          handleMeterTariffErrors(error);
        },
      },
    );
  };

  const handleSubmitTariffData = (onSuccess: () => void) => {
    if (!selectedTariff || !channelId || !channel || !selectedInstallationId) return;

    if (selectedTariff.includes('_DYNAMIC') || selectedTariff === TariffKindInternal.TauronG13S) {
      handleSaveTariff(onSuccess);
      return;
    }

    const values = getValues();
    turnOnBackdrop();

    const tariffConfigurations = omitDeep(
      cloneDeep((channel.data as ChannelMeterType).sharedChannelSettings?.tariffConfigurations || []),
      ['__typename'],
    ) as TariffConfiguration[];

    const index = tariffConfigurations.findIndex((x) => x.tariff === selectedTariff);
    const formData = {
      tariff: selectedTariff,
      prices: values.prices.map((x) => ({ ...x, price: parseFloat(x.price) })),
    };

    if (index !== -1) tariffConfigurations[index] = formData;
    else tariffConfigurations.push(formData);

    updateTariffConfigurations({
      variables: {
        input: {
          channelId,
          installationId: selectedInstallationId,
          tariffConfigurations,
        },
      },
      onCompleted: (data) => {
        turnOffBackdrop();

        if (!data.updateTariffConfigurations) {
          toastError({ content: tc('errors.somethingWentWrong') });
          return;
        }

        const responseData = data.updateTariffConfigurations.meterSharedChannelSettings?.tariffConfigurations as
          | TariffConfiguration[]
          | undefined;

        if (responseData) {
          updateChannelDetails((prev) => {
            const temp = cloneDeep(prev);

            if (temp) {
              (temp.data as ChannelMeterType).sharedChannelSettings = {
                ...(temp.data as ChannelMeterType).sharedChannelSettings,
                tariffConfigurations: responseData,
              };
            }

            return temp;
          });

          if (checkIfItsTheSameTariff(channel)) return;

          handleSaveTariff(onSuccess);
        } else {
          turnOffBackdrop();
          handleErrors(data.updateTariffConfigurations.errors || []);
        }
      },
      onError: () => {
        turnOffBackdrop();
        toastError({ content: tc('errors.somethingWentWrong') });
      },
    });
  };

  return {
    setMeterTariffData,
    handleSubmitTariffData,
  };
};
